import * as THREE from "three";
import { Cell, RoadMap } from "@highmarch/domain";
import { Terrain } from "./terrain";
import { GroundContacts, bakeSlope } from "./groundSlope";
import type { Biome } from "../ui/chapters";
import { GroundMaps } from "./groundMaps";
import { GroundGeometry } from "./groundGeometry";
import { GroundCliffs } from "./groundCliffs";
import { GroundMaterials } from "./groundMaterials";
import { GroundSplat } from "./groundSplat";
import { SurfaceRaster } from "./groundRaster";
import { floorUnder, meshWithin, probeDown } from "./groundProbe";

export { SampledMaps, ShippedOptionalMaps } from "./groundMaps";
export { type GorgeSpot } from "./groundCliffs";
export { windBand } from "./groundMaterials";
export { glowChunks } from "./groundHoles";

/**
 * The ground as ONE continuous mesh instead of a field of 1×1 tiles.
 *
 * Tiles were what made the map read as "thrown together": every cell carried its own baked 1024² patch with its own
 * darkened border and its own turf hummock, so the grid was visible in the texture, in the relief and in the AO. Here
 * the whole island is a single welded surface — vertices are shared between neighbouring cells at the same height, the
 * relief is a smooth world-space noise that crosses cell boundaries, and the colour comes from a handful of *tiling*
 * maps (art/terrain: grass, dirt, cobble, rock) sampled in world space at two scales, so nothing repeats on the cell
 * grid. The road is painted into the same surface through a splat mask drawn from the road spline, which is why it now
 * flows as one track with soft dirt shoulders rather than a line of separate plates.
 *
 * Height steps between plateaus become a continuous cliff band (`cliffs`), built as a strip along every edge where the
 * neighbour is lower — strata displaced horizontally by noise, grass overhanging the lip.
 *
 * The kit's discrete pieces (bridges, ramps, the chasm and lava pools, water) stay as models on top; only the flat
 * grass and path tiles are gone.
 *
 * The ground is built in layers, each a base class of the next so the one mesh keeps one set of state: its geometry
 * (groundGeometry.ts), the gorge walls (groundCliffs.ts), the materials (groundMaterials.ts), the lava, chasm floors
 * and mist (groundHoles.ts), the splat (groundSplat.ts), and here the build and lifecycle. The tiling maps and their
 * cache are groundMaps.ts.
 */
export class GroundMesh extends GroundSplat {
  readonly group = new THREE.Group();
  /** Set once the tiling maps have loaded; until then the caller keeps the old tiles (groundMaps.ts has the cache). */
  static get available(): boolean { return GroundMaps.available; }
  /** The loaded ground and road albedos, so the UI can draw road cards from the same textures the map uses. */
  static get albedos(): { grass: THREE.Texture; road: THREE.Texture; dirt: THREE.Texture } | null { return GroundMaps.albedos; }
  /** The biome whose map set is currently loaded, for debugging and for the headless captures. */
  static get currentBiome(): Biome { return GroundMaps.currentBiome; }
  /** Loads a biome's tiling map set and makes it the one the ground renders with (GroundMaps.load). */
  static load(biome: Biome = "meadow"): Promise<boolean> { return GroundMaps.load(biome); }
  /** Frees every cached biome's maps except `keep`'s (GroundMaps.release); `null` keeps none (call `drop` first). */
  static release(keep: Biome | null): Biome[] { return GroundMaps.release(keep); }
  /** True when a biome's maps are decoded and ready, so `load` for it costs nothing. */
  static isCached(biome: Biome): boolean { return GroundMaps.isCached(biome); }
  /** Stops rendering with the current maps so `release` may free them too (GroundMaps.drop). */
  static drop(): void { GroundMaps.drop(); }
  /** The device's fragment texture-unit limit (16 on iPhone); SceneRig sets it from the renderer. The materials read it. */
  static override get maxSamplers(): number { return GroundMaterials.maxSamplers; }
  static override set maxSamplers(n: number) { GroundMaterials.maxSamplers = n; }

  private surface: THREE.Mesh | null = null;
  private cliffs: THREE.Mesh | null = null;
  private lava: THREE.Mesh | null = null;
  private pitFloor: THREE.Mesh | null = null;
  private mist: THREE.Mesh | null = null;
  /** The ground currently in the scene, for `surfaceAt` (null between maps). */
  private static live: GroundMesh | null = null;
  /** Fixed-point steps `surfaceAt` takes to undo the lateral wander; the third moves the answer by well under 1 mm. */
  private static readonly UnwarpSteps = 3;
  /** The drawn sheet baked for `drawnAt`, over the playable grid and this many cells past it, at this many samples a unit. */
  private raster: SurfaceRaster | null = null;
  private static readonly RasterMargin = 2;
  private static readonly RasterPerUnit = 8;

  /**
   * World y of the ground exactly as drawn under a world point — the triangles on screen, baked (groundRaster.ts) —
   * for what must stand ON the sheet rather than near it: the walking enemies (views/groundFollow.ts). Over a hole
   * (a bridged chasm) and off the baked area it is `surfaceAt`.
   */
  static drawnAt(x: number, z: number): number {
    const r = GroundMesh.live?.raster;
    if (!r) return GroundMesh.surfaceAt(x, z);
    const y = r.at(x, z);
    if (!Number.isNaN(y)) {
      // on a bridge's seat the planks may stand a little proud of a bank that falls away: walk on the planks
      const d = GroundMesh.live!.terrain.deckOver(x, z);
      return d > y ? d : y;
    }
    // over a hole inside the baked area the drawn thing is a plank deck or a chasm floor: the terrain's own height,
    // which (unlike surfaceAt's undoing of the wander) allocates nothing
    return r.covers(x, z) ? GroundMesh.live!.terrain.heightAt(x, z) : GroundMesh.surfaceAt(x, z);
  }

  /**
   * The drawn ground sheet under a world point, NaN over a hole (a chasm or lava pool, bridged or not): what the
   * bridges are fitted to (bridgeFit.ts). Without a built ground, the terrain's own cells say where the holes are.
   */
  /** True once a ground sheet is built and baked (`sheetAt` then reads the drawn triangles, not the cells). */
  static get drawn(): boolean { return !!GroundMesh.live?.raster; }
  static sheetAt(x: number, z: number): number {
    const r = GroundMesh.live?.raster;
    if (r && r.covers(x, z)) return r.at(x, z);
    const t = GroundMesh.live?.terrain ?? Terrain.current;
    if (!t) return 0;
    const c = new Cell(Math.round(x), Math.round(z));
    return t.grid.inBounds(c) && !t.solid(c) ? NaN : t.hillAt(x, z);
  }

  /**
   * World y of the highest solid thing under a world point looking down from `fromY`: the ground sheet, a gorge wall's
   * ledge or batter, or the gorge floor — where a bridge post comes to rest. The walls and floor are ray-cast (a few
   * thousand triangles, and only when a bridge is laid); the sheet is the baked raster.
   */
  static solidBelow(x: number, z: number, fromY: number): number { return GroundMesh.probeNear(-Infinity, -Infinity, Infinity, Infinity)(x, z, fromY); }

  /**
   * `solidBelow` for points within [x0, x1] × [z0, z1] only, against just the walls and floor in that box — what a
   * bridge fit asks (bridgeFit.ts): the one probe set is cut once and every ray after it is cheap. The caller drops the
   * returned function when done (its box's geometry goes with it).
   */
  static probeNear(x0: number, z0: number, x1: number, z1: number): (x: number, z: number, fromY: number) => number {
    const g = GroundMesh.live;
    const t = g?.terrain ?? Terrain.current;
    const whole = !Number.isFinite(x0) || !Number.isFinite(x1) || !Number.isFinite(z0) || !Number.isFinite(z1);
    const parts = g ? [g.cliffs, g.pitFloor].filter((m): m is THREE.Mesh => m !== null).map((m) => whole ? m : meshWithin(m, x0, z0, x1, z1)) : [];
    return (x, z, fromY) => probeDown(parts, GroundMesh.sheetAt(x, z), t ? floorUnder(t, x, z) : 0, x, z, fromY);
  }

  /**
   * World y of the ground as drawn under a world point: the rolling hills plus the micro-relief, sampled where the
   * lateral wander actually put that point. `groundAt` (terrain.ts) is the hills alone — up to ±0.09 off the drawn
   * sheet, which a soldier hides in its stride but which left a 0.15-tall rabbit hovering or sunk to its back
   * (critters.ts). A bridge deck or chasm floor answers with the terrain's own height; no ground built, `groundAt`.
   */
  static surfaceAt(x: number, z: number): number {
    const g = GroundMesh.live;
    const t = g?.terrain ?? Terrain.current;
    if (!g || !t) return t ? t.heightAt(x, z) : 0;
    const h = t.heightAt(x, z);
    if (h !== t.hillAt(x, z)) return h;
    // The vertex drawn at p was built at u, with u + warp(u) = p: a few fixed-point steps find u.
    let ux = x, uz = z;
    for (let i = 0; i < GroundMesh.UnwarpSteps; i++) { const [dx, dz] = g.warpAt(ux, uz); ux = x - dx; uz = z - dz; }
    return g.heightIn(ux, uz);
  }

  /** Builds (or rebuilds) the surface and cliffs. Call again when the road changes to repaint the splat. */
  build(road: RoadMap | null): void {
    const maps = GroundMaps.maps;
    if (!maps) return;
    if (!this.surface) {
      this.splatCanvas = document.createElement("canvas");
      this.paintSplat(road);                                   // creates the splat texture the material binds
      this.buildLand();
      // the slope only matters where the ground is not already lost in the haze
      const F = GroundGeometry.FogFull;
      this.slope = bakeSlope((x, z) => this.heightIn(x, z), -0.5 - F, -0.5 - F, this.grid.width - 0.5 + F, this.grid.height - 0.5 + F);
      this.lavaSpots = this.lavaClusters();
      this.restamp();
      this.surface = new THREE.Mesh(this.buildSurface(), this.surfaceMaterial(maps));
      this.surface.receiveShadow = true;
      this.surface.castShadow = false;
      this.raster = this.bakeRaster(this.surface.geometry);
      GroundMesh.live = this;
      // Props are placed in no fixed order relative to the ground (the level dresses the land past the map after
      // building it; the hub builds its props after its ground), so the contact shadows are re-stamped whenever the
      // registry has moved on — checked here, the one place that runs exactly when the ground is about to be drawn.
      this.surface.onBeforeRender = () => { if (GroundContacts.version !== this.stampedVersion) this.restamp(); };
      this.cliffs = new THREE.Mesh(this.buildCliffs(), this.cliffMaterial(maps));
      this.cliffs.receiveShadow = true;
      this.cliffs.castShadow = true;
      this.group.add(this.surface, this.cliffs);
      const lava = this.buildLava();
      if (lava) { this.lava = lava; this.group.add(lava); }
      const floor = this.buildPitFloor(maps);
      if (floor) { this.pitFloor = floor; this.group.add(floor); }
      const mist = this.buildPitMist();
      if (mist) { this.mist = mist; this.group.add(mist); }
    }
    this.paintSplat(road);
  }

  private bakeRaster(g: THREE.BufferGeometry): SurfaceRaster | null {
    const idx = g.index;
    if (!idx) return null;
    const m = GroundMesh.RasterMargin;
    return SurfaceRaster.bake(g.attributes.position.array, idx.array, -0.5 - m, -0.5 - m, this.grid.width - 0.5 + m, this.grid.height - 0.5 + m, 1 / GroundMesh.RasterPerUnit);
  }

  dispose(): void {
    for (const m of [this.surface, this.cliffs, this.lava, this.pitFloor, this.mist]) {
      if (!m) continue;
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
    this.splat?.dispose();
    this.splat = null;
    this.splatRGB = null;
    this.slope?.texture.dispose();
    this.slope = null;
    this.raster = null;
    if (GroundGeometry.currentLand === this.land) { GroundGeometry.currentLand = null; GroundCliffs.currentGorge = []; }
    if (GroundMesh.live === this) GroundMesh.live = null;
    this.group.clear();
    this.surface = this.cliffs = this.lava = this.pitFloor = this.mist = null;
  }

  /** Advances the molten surface. */
  update(t: number): void {
    const m = this.lava as (THREE.Mesh & { tick?: (t: number) => void }) | null;
    m?.tick?.(t);
  }
}
